
import { jwtDecode } from 'jwt-decode';

interface DecodedToken {
  /** Employee HR record ID - use for EM/Payroll API calls */
  primarysid: string;
  /** User account ID - use for UserManagement API calls */
  nameid: string;
  email: string;
  role: string | string[];
}

/**
 * Safely decode the JWT - returns null instead of throwing if token
 * is missing or malformed. Replaces the old throwing version that caused
 * unhandled rejections across 40+ service files.
 */
const decodeToken = (): DecodedToken | null => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    return jwtDecode<DecodedToken>(token);
  } catch {
    return null;
  }
};

/**
 * Returns employee HR record ID (primarysid).
 * Use for EM / Payroll / Attendance API calls.
 * Returns null if token is missing - callers should handle null.
 */
export const getUserIdFromToken = (): string | null => {
  return decodeToken()?.primarysid ?? null;
};

/**
 * Explicit alias for the employee record ID - same as getUserIdFromToken.
 */
export const getEmployeeIdFromToken = (): string | null => {
  return decodeToken()?.primarysid ?? null;
};

/**
 * Returns the user ACCOUNT ID (nameid).
 * Use for UserManagement (permissions, roles, users) API calls.
 * Different from employee ID - nameid=9, primarysid=2.
 */
export const getUserAccountIdFromToken = (): string | null => {
  return decodeToken()?.nameid ?? null;
};

/**
 * Returns EVERY role the token says the account holds, as a raw list.
 *
 * The `role` claim arrives in three shapes and they are not interchangeable: a
 * plain string, a comma-joined string ("Accountant,Employee" — what a multi-role
 * .NET JWT produces), or an array (what `jwt-decode` yields when the claim
 * repeats). Callers that only read the first entry see an Accountant as
 * "Accountant" and never learn they are also an Employee — which is exactly how
 * 13 self-service routes became unreachable for non-Employee roles (task #49).
 *
 * This is the ENTITLEMENT set — every route/permission check gates on it. For
 * UI that needs exactly one role to display (there is no stored "active" role),
 * see src/auth/rolePriority.ts. Returns [] when the token is missing or malformed.
 */
export const getRolesFromToken = (): string[] => {
  const claim = decodeToken()?.role;
  if (claim === null || claim === undefined) return [];
  return (Array.isArray(claim) ? claim : [claim])
    .flatMap((v) => String(v ?? '').split(','))
    .map((r) => r.trim())
    .filter(Boolean);
};